import { Injectable, inject } from "@angular/core";
import { Store } from "@ngxs/store";
import { NavigationEnd, Router } from "@angular/router";
import { MessageService } from "primeng/api";
import {
  EMPTY,
  distinctUntilChanged,
  filter,
  interval,
  map,
  merge,
  startWith,
  switchMap,
  tap,
} from "rxjs";
import { AuthState } from "./auth.state";
import { UpdateUserInfo } from "./auth.actions";
import { UserGroup } from "@platform-app/app/core/api/models";

@Injectable({ providedIn: "root" })
export class InactiveStudentSessionService {
  private readonly store = inject(Store);
  private readonly router = inject(Router);
  private readonly messageService = inject(MessageService);

  watch(): void {
    const navigationEnd$ = this.router.events.pipe(
      filter((event) => event instanceof NavigationEnd),
    );

    this.store
      .select(AuthState.user)
      .pipe(
        map((user) => user?.roleGroup ?? null),
        distinctUntilChanged(),
        switchMap((roleGroup) =>
          roleGroup === UserGroup.Student
            ? merge(interval(60_000), navigationEnd$).pipe(startWith(0))
            : EMPTY,
        ),
        switchMap(() => {
          const wasInactive = this.store.selectSnapshot(
            AuthState.user,
          )?.isInactive;

          return this.store
            .dispatch(new UpdateUserInfo())
            .pipe(map(() => wasInactive));
        }),
        map(
          (wasInactive) =>
            [
              wasInactive,
              this.store.selectSnapshot(AuthState.user)?.isInactive,
            ] as const,
        ),
        filter(([wasInactive, isNowInactive]) => wasInactive !== isNowInactive),
        tap(([, isNowInactive]) => {
          if (isNowInactive) {
            this.router.navigate(["/"]);
            this.messageService.add({
              severity: "info",
              summary: "Access changed",
              detail:
                "Your portal is now in read-only mode while your placement is inactive.",
            });
          } else {
            this.messageService.add({
              severity: "success",
              summary: "Access restored",
              detail: "Your access has been restored.",
            });
          }
        }),
      )
      .subscribe();
  }
}
